import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";
import { useAuth } from "../auth/AuthContext";
import { phase1Api } from "../api/client";

const optionalToggles = [
  ["promotional_offers", "Promotional offers"],
  ["new_product_alerts", "New product alerts"],
  ["marketing_updates", "Marketing updates"],
  ["email_notifications", "Email notifications"],
  ["email_promotional", "Email marketing"],
];

const audienceOptions = ["EVERYONE", "BUYERS", "SELLERS", "BUSINESSES", "TRANSPORTERS", "ADMINS"];
const categoryOptions = ["PLATFORM", "ORDER", "PAYMENT", "DELIVERY", "ACCOUNT", "SECURITY", "PROMOTIONAL", "NEW_PRODUCT", "MARKETING"];

function isStaff(user) {
  const type = String(user?.account_type || "").toUpperCase();
  return Boolean(user?.is_platform_staff || user?.is_superuser || ["SUPER_ADMIN", "ADMIN", "FINANCE", "SUPPORT", "OPERATIONS"].includes(type));
}

function pretty(value) {
  return String(value || "").replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
}

function timeLabel(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function internalLink(value) {
  return typeof value === "string" && value.startsWith("/") && !value.startsWith("//")
    ? value
    : null;
}

export default function NotificationCentre() {
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState(null);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [broadcast, setBroadcast] = useState({ audience: "EVERYONE", category: "PLATFORM", title: "", message: "", link: "/portal/notifications", send_email: false });
  const staff = useMemo(() => isStaff(user), [user]);

  async function loadData(filter = activeFilter) {
    setLoading(true);
    try {
      const params = filter !== "ALL" ? { category: filter } : {};
      const [listRes, prefRes] = await Promise.all([
        phase1Api.notifications(params),
        phase1Api.notificationPreferences(),
      ]);
      setNotifications(listRes.data?.results || []);
      setUnreadCount(listRes.data?.unread_count || 0);
      setPreferences(prefRes.data || null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData("ALL");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function changeFilter(next) {
    setActiveFilter(next);
    await loadData(next);
  }

  async function markRead(item) {
    if (item.is_read) return;
    await phase1Api.markNotificationRead(item.id);
    setNotifications((current) => current.map((row) => row.id === item.id ? { ...row, is_read: true } : row));
    setUnreadCount((count) => Math.max(0, count - 1));
  }

  async function markAllRead() {
    await phase1Api.markAllNotificationsRead();
    setNotifications((current) => current.map((row) => ({ ...row, is_read: true })));
    setUnreadCount(0);
  }

  async function updatePreference(key, value) {
    setSaving(true);
    try {
      const next = { ...(preferences || {}), [key]: value };
      const res = await phase1Api.updateNotificationPreferences(next);
      setPreferences(res.data);
    } finally {
      setSaving(false);
    }
  }

  async function sendBroadcast(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await phase1Api.broadcastNotification(broadcast);
      setBroadcast({ audience: "EVERYONE", category: "PLATFORM", title: "", message: "", link: "/portal/notifications", send_email: false });
      await loadData(activeFilter);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={`portal-shell-v2 refined-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <PortalSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((value) => !value)} />
      <main className="portal-main-v2 refined-main professional-main">
        <PortalTopbar user={user} onLogout={logout} />

        <section className="notification-page-hero">
          <div>
            <span>TAZY NOTIFICATIONS</span>
            <h2>Notification Centre</h2>
            <p>Track order, payment, delivery, account and platform updates from one place.</p>
          </div>
          <div className="notification-hero-count">
            <strong>{unreadCount}</strong>
            <span>Unread</span>
          </div>
        </section>

        <section className="notification-layout-grid">
          <div className="notification-main-panel">
            <div className="notification-panel-head">
              <div>
                <span>Live updates</span>
                <h3>Recent notifications</h3>
              </div>
              <div className="notification-filter-row">
                {["ALL", "ORDER", "PAYMENT", "DELIVERY", "DOCUMENT", "ACCOUNT", "SECURITY"].map((item) => (
                  <button type="button" key={item} className={activeFilter === item ? "active" : ""} onClick={() => changeFilter(item)}>{pretty(item)}</button>
                ))}
              </div>
            </div>
            {unreadCount > 0 && <button type="button" className="notification-mark-all" onClick={markAllRead}>Mark all as read</button>}

            {loading ? <p className="notification-muted">Loading notifications...</p> : notifications.length === 0 ? (
              <div className="notification-empty-state">No notifications for this view.</div>
            ) : (
              <div className="notification-card-list">
                {notifications.map((item) => (
                  <article key={item.id} className={`notification-card-row ${item.is_read ? "is-read" : "is-unread"}`}>
                    <button type="button" onClick={() => markRead(item)} className="notification-card-main">
                      <span>{pretty(item.category)} • {pretty(item.priority)}</span>
                      <strong>{item.title}</strong>
                      <p>{item.message}</p>
                      <small>{timeLabel(item.created_at)}</small>
                    </button>
                    {internalLink(item.link) && <Link to={internalLink(item.link)} onClick={() => markRead(item)}>Open</Link>}
                  </article>
                ))}
              </div>
            )}
          </div>

          <aside className="notification-side-panel">
            <div className="notification-settings-card">
              <span>Preferences</span>
              <h3>Notification controls</h3>
              <div className="notification-toggle-list">
                {optionalToggles.map(([key, label]) => (
                  <label key={key}>
                    <span>{label}</span>
                    <input type="checkbox" checked={Boolean(preferences?.[key])} disabled={!preferences || saving} onChange={(event) => updatePreference(key, event.target.checked)} />
                  </label>
                ))}
              </div>
            </div>

            {staff && (
              <form className="notification-settings-card broadcast-card" onSubmit={sendBroadcast}>
                <span>Admin broadcast</span>
                <h3>Send platform notice</h3>
                <select value={broadcast.audience} onChange={(event) => setBroadcast((current) => ({ ...current, audience: event.target.value }))}>
                  {audienceOptions.map((item) => <option key={item} value={item}>{pretty(item)}</option>)}
                </select>
                <select value={broadcast.category} onChange={(event) => setBroadcast((current) => ({ ...current, category: event.target.value }))}>
                  {categoryOptions.map((item) => <option key={item} value={item}>{pretty(item)}</option>)}
                </select>
                <input value={broadcast.title} onChange={(event) => setBroadcast((current) => ({ ...current, title: event.target.value }))} placeholder="Notice title" />
                <textarea value={broadcast.message} onChange={(event) => setBroadcast((current) => ({ ...current, message: event.target.value }))} placeholder="Message" rows="4" />
                <input value={broadcast.link} onChange={(event) => setBroadcast((current) => ({ ...current, link: event.target.value }))} placeholder="Open link" />
                <label className="broadcast-check"><input type="checkbox" checked={broadcast.send_email} onChange={(event) => setBroadcast((current) => ({ ...current, send_email: event.target.checked }))} /> Send email too</label>
                <button type="submit" disabled={saving || !broadcast.title || !broadcast.message}>Send notice</button>
              </form>
            )}
          </aside>
        </section>
      </main>
    </div>
  );
}
